import { useMemo } from "react";

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";
import { differenceInMilliseconds } from "date-fns";
import { Button, Card, Divider, Loader } from "semantic-ui-react";

import { initUseQuery } from "simplydo/core/query";

import api from "api";
import { useAppSelector } from "store";

import ChallengeCard from "components/lib/Cards/ChallengeCard";
import { EmptyBox } from "components/lib/UI";

const PublicChallenges = () => {
  const { t } = useTranslation();

  const organisation = useAppSelector(
    (state) => state.organisations.urlOrganisation,
  );
  const user = useAppSelector((state) => state.user);

  const shopWindowQuery = useQuery(
    initUseQuery(api.organisations.getShopWindow, [organisation!._id], {
      isEnabled: !!organisation,
    }),
  );

  const challenges = useMemo(
    () => shopWindowQuery.data?.challenges ?? [],
    [shopWindowQuery.data?.challenges],
  );

  const openChallenges = useMemo(
    () =>
      challenges
        .filter((challenge) => challenge.stage === "published")
        .sort((a, b) =>
          differenceInMilliseconds(
            new Date(b.createdAt),
            new Date(a.createdAt),
          ),
        ),
    [challenges],
  );
  const closedChallenges = useMemo(
    () =>
      challenges
        .filter((challenge) => challenge.stage === "closed")
        .sort((a, b) =>
          differenceInMilliseconds(
            new Date(b.createdAt),
            new Date(a.createdAt),
          ),
        ),
    [challenges],
  );

  return (
    <div style={{ paddingLeft: 30 }}>
      <h1 className="title" style={{ color: "#3b3b3b" }}>
        Public {t("generic.challenges")}
      </h1>
      {!user && (
        <Link to="/">
          <p style={{ fontSize: 14 }}>
            <u>Log in or sign up to discover more</u>
          </p>
        </Link>
      )}
      {user && (
        <Link to="/challenges/discover">
          <p style={{ fontSize: 14 }}>
            <u>Discover more {t("generic.challenges")}</u>
          </p>
        </Link>
      )}
      <Divider />

      {shopWindowQuery.isLoading ?
        <Loader style={{ marginTop: 20 }} active inline="centered" />
      : <div>
          {organisation ?
            <div>
              {challenges.length >= 1 && (
                <>
                  {openChallenges.length > 0 && (
                    <>
                      <h3>Published {t("generic.challenges")}</h3>
                      <Card.Group itemsPerRow={4} stackable>
                        {openChallenges.map((challenge) => (
                          <ChallengeCard
                            challenge={challenge}
                            key={challenge._id}
                          />
                        ))}
                      </Card.Group>
                    </>
                  )}
                  {closedChallenges.length > 0 && (
                    <>
                      <h3>Closed {t("generic.challenges")}</h3>
                      <Card.Group itemsPerRow={4} stackable>
                        {closedChallenges.map((challenge) => (
                          <ChallengeCard
                            challenge={challenge}
                            key={challenge._id}
                          />
                        ))}
                      </Card.Group>
                    </>
                  )}
                </>
              )}
              {challenges.length === 0 && (
                <EmptyBox style={{ margin: "20px 40px" }}>
                  <h2>
                    There aren&apos;t any public {t("generic.challenges")} right
                    now
                  </h2>
                  {user ?
                    <Button
                      size="large"
                      as={Link}
                      to="/challenges/discover"
                      primary
                      icon="eye"
                      content={`Discover ${t("generic.challenges")}`}
                    />
                  : <Button
                      size="large"
                      as={Link}
                      to="/"
                      primary
                      content={t("entry.login.loginOrSignUp")}
                    />
                  }
                </EmptyBox>
              )}
            </div>
          : <EmptyBox
              style={{ margin: "20px 40px" }}
              title={`No public ${t("generic.challenges")} available`}
            />
          }
        </div>
      }
      <Divider hidden />
    </div>
  );
};

export default PublicChallenges;
